import MUIPersonAddIcon from '@mui/icons-material/PersonAdd';
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from '@mui/material/ListItemIcon';
import DialogIntakeQuestionnaire from "../../../../Dialog/DialogIntakeQuestionnaire";
import CardQueueDataGridOrderActivity from "../CardQueueDataGridOrderActivity";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";
import DialogDatePicker from '../../../../Dialog/DialogDatePicker';
import DialogAtpDateTimePicker from '../../../../Dialog/DialogAtpDateTimePicker';
import DialogEvaluationTypeAndDatePicker from '../../../../Dialog/DialogEvaluationTypeAndDatePicker';
import Validator from '../../../../../Utility/Validator';
import intakeQuestionnaireService from '../../../../../Seating/Security/IntakeQuestionnaireService/intakeQuestionnaireService';
import moment from 'moment';
import api from '../../../../../Seating/Security/api';
import insuranceService from '../../../../../Seating/Security/InsuranceService/insuranceService';
import NoteService from '../../../../../Seating/Security/NoteService/noteService';
import Eligibility from '../../../../../Utility/Eligibility';

export default class CardQueueDataGridOrderActivitySales extends CardQueueDataGridOrderActivity {
    constructor(props) {
        super(props);

        this.columns = {
            ...this.columns,
            "order_activity.scheduled_delivery_date": {
                "type": "dateTime",
                "name": "Scheduled",
                "width": 180,
            },
        };

        this.state.datePickerOpen = false;
        this.state.atpDateTimePickerOpen = false;
        this.state.evaluationTypeAndDatePickerOpen = false;
    }

    /**
     * Handle clicking on a grid row.
     */
    handleClickRow(row) {
        this.setState({
            clickedRow: row
        });

        if (this.props.onClickRow) {
            this.props.onClickRow();
        }

        this.openOrder(row);
    }

    /**
     * Opens the order.
     *
     * @param {Object} row The grid row.
     */
    openOrder(row) {
        window.open(`/chart/${row["order_activity.patient_id"]}/order/${row["order_activity.id"]}`, "_blank");

        this.setState({
            queueDirty: true,
        });
    }

    /**
     * Handles the opening the Intake Questionnaire dialog.
     *
     * @param {object} row - The data for the current row, typically containing order details.
     */
    async handleOpenIntakeQuestionnaireDialog(row) {
        const currentIntakeQuestionnaireId = row['order_activity.current_intake_questionnaire_id'];
        const orderId = row["order_activity.id"];

        this.setState({
            intakeQuestionnaireDialogOpen: true,
            row: row,
            rowDataLoading: true,
        });

        try {
            // Fetch patient and order data in parallel
            const order = await api.send("GET", `/order?id=${orderId}`);

            // If there's an intake questionnaire, fetch it
            let intakeQuestionnaire = null;
            if (currentIntakeQuestionnaireId) {
                const response = await intakeQuestionnaireService.getIntakeQuestionnaire(currentIntakeQuestionnaireId);
                intakeQuestionnaire = JSON.parse(response.jsonResponse);
            }

            this.setState({
                order: order,
                intakeQuestionnaire: intakeQuestionnaire,
                readOnly: !!currentIntakeQuestionnaireId,
                rowDataLoading: false,
            });

        } catch (err) {
            console.error("Failed to fetch data:", err);
            NotistackEnqueueSnackbar("Oh no! There was an error loading the data.", { variant: "error" });
            this.setState({
                rowDataLoading: false,
                readOnly: !!currentIntakeQuestionnaireId
            });
        }
    }

    /**
     * Handles the opening of a date picker or date-time picker.
     *
     * @param {boolean} isDateTimePicker - Indicates if the date-time picker
     * should be opened.
     * @param {object} row - The data for the current row, typically containing
     * order details.
     * @param {string} columnName - The name of the column associated with the
     * date selection.
     */
    handleOpenDatePicker(row, columnName, defaultValue) {
        const columnNameMap = {
            "order_activity.evaluation_date": "evaluationDate",
            "order_activity.face_to_face_date": "faceToFaceDate",
            "order_activity.atp_evaluation_date": "atpEvaluationDate",
        };

        // Update columnName based on the mapping or keep it unchanged
        const updatedColumnName = columnNameMap[columnName] || columnName;

        this.setState({
            datePickerOpen: columnName !== "order_activity.atp_evaluation_date",
            row: row,
            columnName: updatedColumnName,
            defaultValue: defaultValue
        });
    };

    /**
     * Handles the opening of the atp date picker.
     *
     * @param {object} row - The data for the current row, typically containing
     * order details.
     * @param {string} columnName - The name of the column associated with the
     * date selection.
     */
    handleOpenAtpDatePicker(row, columnName, defaultValue) {
        const columnNameMap = {
            "order_activity.evaluation_date": "evaluationDate",
            "order_activity.face_to_face_date": "faceToFaceDate",
            "order_activity.atp_evaluation_date": "atpEvaluationDate",
        };
        const rowDictionary = this.props.rows.reduce((acc, row) => {
            acc[row['order_activity.id']] = row;
            return acc;
        }, {});
        const unformattedRow = rowDictionary[row["order_activity.id"]];

        // Update columnName based on the mapping or keep it unchanged
        const updatedColumnName = columnNameMap[columnName] || columnName;

        this.setState({
            atpDateTimePickerOpen: columnName === "order_activity.atp_evaluation_date",
            row: row,
            unformattedRow: unformattedRow,
            columnName: updatedColumnName,
            defaultValue: defaultValue
        });
    };

    /**
     * Handles the opening of a date picker or date-time picker.
     *
     * @param {boolean} isDateTimePicker - Indicates if the date-time picker
     * should be opened.
     * @param {object} row - The data for the current row, typically containing
     * order details.
     * @param {string} columnName - The name of the column associated with the
     * date selection.
     */
    handleOpenEvaluationTypeAndDatePicker(row, columnName, defaultValue) {
        const columnNameMap = {
            "order_activity.evaluation_date": "evaluationDate",
            "order_activity.face_to_face_date": "faceToFaceDate",
            "order_activity.atp_evaluation_date": "atpEvaluationDate",
        };

        // Update columnName based on the mapping or keep it unchanged
        const updatedColumnName = columnNameMap[columnName] || columnName;

        this.setState({
            evaluationTypeAndDatePickerOpen: columnName === "order_activity.evaluation_date",
            row: row,
            columnName: updatedColumnName,
            defaultValue: defaultValue
        });
    };

    /**
     * Creates an activity note for the order.
     *
     * @param {object} order - The order to create the note for.
     */
    handleSaveActivityNote(order) {
        let note = {
            task: false,
            completed: true,
            assignedToId: null,
            createdBy: this.context.currentUser,
            createdById: this.context.currentUser.id,
            accountId: order && order.activity ? order.activity.accountId : null,
            patientName: order ? order.patientName : "",
            type: "ActivityNote",
            activityId: order?.activity?.id || null,
            dueDate: null,
            isAutoVerification: true,
            text: this.state.noteText,
        };

        return NoteService.createActivityNote(order.id, note)
            .then((res) => {
                if (res) {
                    if (res.createdAt.indexOf("Z") > 0) {
                        res.createdAt = res.createdAt.split("Z")[0];
                    }
                }
            })
            .catch((err) => {
                console.log(err);
            });
    }

    /**
     * Creates a verification note for the order.
     *
     * @param {object} order - The order to create the note for.
     */
    handleVerificationNoteCreation(order) {
        let noteText = `${order.id} ${order.patientName}\nCoverage Summary\n`;
        let address = null;
        const sameSimResult = Eligibility.getSameSim(order.apiResults);
        if (sameSimResult) {
            noteText += `\nSame/Sim Status: ${sameSimResult.status ?? "Unknown"}`;
            if (sameSimResult.status === "processed") {
                noteText += `\nSame/Sim Eligible: ${sameSimResult.eligible ? "Yes" : "No"}`;
            }
            if (sameSimResult.detail?.errorMessage) {
                noteText += `\nSame/Sim Message: ${sameSimResult.detail.errorMessage}`;
            }
            if (sameSimResult.status === "warning" && Array.isArray(sameSimResult.detail?.claims)) {
                noteText += `\nSame/Sim Wheelchair Claims on File (Past 5 Years):`;
                sameSimResult.detail.claims.forEach((claim) => {
                    noteText += `\n - ${claim.hcpcsCode} on ${moment(claim.dosStart).format("MM/DD/YYYY")}`;
                });
            }

            noteText += `\n`;
        }

        // Add primary insurance summary if it exists
        const primaryInsurance = order.verifiedInsurance.find(insurance => insurance.type === "primary" || insurance.type === "insurance");
        if (primaryInsurance?.results) {
            const primary = primaryInsurance.results;
            noteText += `\nPrimary: ${this.context.insuranceTypesIndexed[order.insuranceTypeId]?.name} - ${this.context.insuranceSubTypesIndexed[order.insuranceSubTypeId]?.name}`;
            noteText += `\nPlan: ${primary.detail.planName || "N/A"}`;

            if (primary.detail.isMedicare) {
                noteText += `\nDeductible Part B: ${primary.detail.deductible.partB !== null ?
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(primary.detail.deductible.partB) :
                    "N/A"}`;
            } else {
                noteText += `\nDeductible: ${primary.detail.deductible.inNetwork !== null ?
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(primary.detail.deductible.inNetwork) :
                    "N/A"}`;
            }

            noteText += `\nCoinsurance: ${primary.detail.dmeSummary?.coInsuranceInNetwork || "N/A"}`;

            // Add raw additionalInfo string if present
            if (primary.detail?.additionalInfo) {
                // Try to get the raw string version if possible
                let rawAdditionalInfo = "";
                if (primary.detail.noteData?.AdditionalInfo) {
                    rawAdditionalInfo = primary.detail.noteData.AdditionalInfo;
                } else if (primary.detail.noteData?.AddtionalInfo) {
                    rawAdditionalInfo = primary.detail.noteData.AddtionalInfo;
                }
                if (rawAdditionalInfo && typeof rawAdditionalInfo === "string") {
                    noteText += `\nAdditional Info:${rawAdditionalInfo}\n`;
                }
            }
        }

        // Add secondary insurance summary if it exists
        const secondaryInsurance = order.verifiedInsurance.find(insurance => insurance.type === "secondary" || insurance.type === "secondaryInsurance");
        if (secondaryInsurance?.results) {
            const secondary = secondaryInsurance.results;
            noteText += `\nSecondary: ${this.context.insuranceTypesIndexed[order.secondaryInsuranceTypeId]?.name} - ${this.context.insuranceSubTypesIndexed[order.secondaryInsuranceSubTypeId]?.name}`;
            noteText += `\nPlan: ${secondary.detail.planName || "N/A"}`;

            if (secondary.detail.isMedicare) {
                noteText += `\nDeductible Part B: ${secondary.detail.deductible.partB !== null ?
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(secondary.detail.deductible.partB) :
                    "N/A"}`;
            } else {
                noteText += `\nDeductible: ${secondary.detail.deductible.inNetwork !== null ?
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(secondary.detail.deductible.inNetwork) :
                    "N/A"}`;
            }

            noteText += `\nCoinsurance: ${secondary.detail.dmeSummary?.coInsuranceInNetwork || "N/A"}`;

            // Add raw additionalInfo string if present
            if (secondary.detail?.additionalInfo) {
                let rawAdditionalInfo = "";
                if (secondary.detail.noteData?.AdditionalInfo) {
                    rawAdditionalInfo = secondary.detail.noteData.AdditionalInfo;
                } else if (secondary.detail.noteData?.AddtionalInfo) {
                    rawAdditionalInfo = secondary.detail.noteData.AddtionalInfo;
                }
                if (rawAdditionalInfo && typeof rawAdditionalInfo === "string") {
                    noteText += `\nAdditional Info:${rawAdditionalInfo}\n`;
                }
            }
        }

        if (order.eligibilitySummaryText) {
            noteText += `\n${order.eligibilitySummaryText}\n`;
        }

        // Add detailed note data for primary insurance
        if (primaryInsurance?.results?.detail?.noteData) {
            const p = primaryInsurance.results.detail?.noteData;
            if (p.DemographicInfo && p.DemographicInfo.Subscriber && p.DemographicInfo.Subscriber.Address1) {
                noteText += "\nPrimary Member ID: " + (p.DemographicInfo.Subscriber.Identification && p.DemographicInfo.Subscriber.Identification.length > 0 ? p.DemographicInfo.Subscriber.Identification.filter(x => x.Type === "Member ID")[0].Code : order.insurancePolicy);
                address = "\n" + (p.DemographicInfo.Subscriber.Address1 || "") + "\n" +
                    (p.DemographicInfo.Subscriber.Address2 || "") + "\n" +
                    (p.DemographicInfo.Subscriber.City || "") + ", " +
                    (p.DemographicInfo.Subscriber.State || "") + " " +
                    (p.DemographicInfo.Subscriber.Zip || "");
            }
            else {
                noteText += "\nMember ID: " + order.insurancePolicy;
                address = "\n" + order.address + "\n" + order.city + ", " + order.state + " " + order.zipCode;
            }

            //if (currentCoverage) {
            if (p.PlanCoverageSummary) {
                [p.PlanCoverageSummary].map(v => {
                    var ll = [];
                    for (var oKey in v) {
                        if (v[oKey] !== null) {
                            ll.push({
                                name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: v[oKey],
                            });
                        }
                    }
                    if (ll && ll.length > 0) {
                        return ll.map(l => {
                            return noteText += "\n" + l.name + ": " + l.value;
                        });
                    }
                    return '';
                });
            }

            if (p.OtherPayerInfo) {
                [p.OtherPayerInfo].map(d => {
                    var res = [];
                    for (var pkey in d) {
                        if (d[pkey] !== null && d[pkey] !== "None") {
                            res.push({
                                name: pkey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: d[pkey],
                            });
                        }
                    }
                    if (res && res.length > 0) {
                        return res.map(l => {
                            return noteText += "\n" + l.name + ": " + l.value;
                        });
                    }
                    return null;
                });
            }

            if (p.MedicareInfoSummary) {
                [p.MedicareInfoSummary].map(v => {
                    var ll = [];
                    for (var oKey in v) {
                        if (oKey.toLowerCase().indexOf("part_a") === -1 &&
                            oKey.toLowerCase().indexOf("pt_slp_") === -1 &&
                            oKey.toLowerCase().indexOf("ot_capamount") === -1) {
                            if (v[oKey] && typeof v[oKey] === "object" && v[oKey].hasOwnProperty("Value")) {
                                ll.push({
                                    name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                    value: v[oKey].Value,
                                    note: v[oKey].Notes,
                                });
                            }
                            else if (v[oKey] !== null && v[oKey] !== "None" &&
                                oKey.toLowerCase().indexOf("pharmacy") < 0 &&
                                !["bin", "pcn"].includes(oKey.toLowerCase())) {
                                ll.push({
                                    name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                    value: v[oKey],
                                    note: null,
                                });
                            }
                        }
                    }
                    if (ll && ll.length > 0) {
                        return ll.map(l => {
                            return noteText += "\n" + l.name + ": " + l.value;
                        });
                    }
                    return '';
                });
            }

            if (p.PCPAuthInfoSummary) {
                [p.PCPAuthInfoSummary].map(d => {
                    var res = [];
                    for (var pkey in d) {
                        if (d[pkey] !== null && d[pkey] !== "None") {
                            res.push({
                                name: pkey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: d[pkey],
                            });
                        }
                    }
                    if (res && res.length > 0) {
                        return res.map(l => {
                            return noteText += "\n" + l.name + ": " + l.value;
                        });
                    }
                    return null;
                });
            }

            if (p.MiscellaneousInfoSummary) {
                [p.MiscellaneousInfoSummary].map(d => {
                    var res = [];
                    for (var pkey in d) {
                        if (d[pkey] !== null) {
                            res.push({
                                name: pkey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: d[pkey],
                            });
                        }
                    }
                    if (res && res.length > 0) {
                        return res.map(r => {
                            return noteText += "\n" + r.name + ": " + r.value;
                        });
                    }
                    return '';
                });
            }

            if (p.HBPC_Deductible_OOP_Summary) {
                [p.HBPC_Deductible_OOP_Summary].map(v => {
                    var ll = [];
                    for (var oKey in v) {
                        if (v[oKey] && typeof v[oKey] === "object" && v[oKey].hasOwnProperty("Value")) {
                            ll.push({
                                name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: v[oKey].Value,
                            });
                        }
                        else if (v[oKey] !== null) {
                            ll.push({
                                name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: v[oKey],
                            });
                        }
                    }
                    if (ll && ll.length > 0) {
                        return ll.map(l => {
                            return noteText += "\n" + l.name + ": " + l.value;
                        });
                    }
                    return '';
                });
            }

            if (p.DMESummary) {
                [p.DMESummary].map(v => {
                    var ll = [];
                    for (var oKey in v) {
                        if (v[oKey] && typeof v[oKey] === "object" && v[oKey].hasOwnProperty("Value")) {
                            ll.push({
                                name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: v[oKey].Value,
                                note: v[oKey].Notes,
                            });
                        }
                        else if (v[oKey] !== null && v[oKey] !== "None") {
                            ll.push({
                                name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: v[oKey],
                                note: null,
                            });
                        }
                    }
                    if (ll && ll.length > 0) {
                        return ll.map(l => {
                            return noteText += "\n" + l.name + ": " + l.value;
                        });
                    }
                    return '';
                });
            }

            noteText += "\n";
            //}
        }


        // Add detailed note data for secondary insurance
        if (secondaryInsurance?.results?.detail?.noteData) {
            const p = secondaryInsurance.results.detail?.noteData;
            if (p.DemographicInfo && p.DemographicInfo.Subscriber && p.DemographicInfo.Subscriber.Address1) {
                noteText += "\nSecondary Member ID: " + (p.DemographicInfo.Subscriber.Identification && p.DemographicInfo.Subscriber.Identification.length > 0 ? p.DemographicInfo.Subscriber.Identification.filter(x => x.Type === "Member ID")[0].Code : order.insurancePolicy);
                address = "\n" + (p.DemographicInfo.Subscriber.Address1 || "") + "\n" +
                    (p.DemographicInfo.Subscriber.Address2 || "") + "\n" +
                    (p.DemographicInfo.Subscriber.City || "") + ", " +
                    (p.DemographicInfo.Subscriber.State || "") + " " +
                    (p.DemographicInfo.Subscriber.Zip || "");
            }
            else {
                noteText += "\nMember ID: " + order.insurancePolicy;
                address = "\n" + order.address + "\n" + order.city + ", " + order.state + " " + order.zipCode;
            }

            //if (currentCoverage) {
            if (p.PlanCoverageSummary) {
                [p.PlanCoverageSummary].map(v => {
                    var ll = [];
                    for (var oKey in v) {
                        if (v[oKey] !== null) {
                            ll.push({
                                name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: v[oKey],
                            });
                        }
                    }
                    if (ll && ll.length > 0) {
                        return ll.map(l => {
                            return noteText += "\n" + l.name + ": " + l.value;
                        });
                    }
                    return '';
                });
            }

            if (p.OtherPayerInfo) {
                [p.OtherPayerInfo].map(d => {
                    var res = [];
                    for (var pkey in d) {
                        if (d[pkey] !== null && d[pkey] !== "None") {
                            res.push({
                                name: pkey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: d[pkey],
                            });
                        }
                    }
                    if (res && res.length > 0) {
                        return res.map(l => {
                            return noteText += "\n" + l.name + ": " + l.value;
                        });
                    }
                    return null;
                });
            }

            if (p.MedicareInfoSummary) {
                [p.MedicareInfoSummary].map(v => {
                    var ll = [];
                    for (var oKey in v) {
                        if (oKey.toLowerCase().indexOf("part_a") === -1 &&
                            oKey.toLowerCase().indexOf("pt_slp_") === -1 &&
                            oKey.toLowerCase().indexOf("ot_capamount") === -1) {
                            if (v[oKey] && typeof v[oKey] === "object" && v[oKey].hasOwnProperty("Value")) {
                                ll.push({
                                    name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                    value: v[oKey].Value,
                                    note: v[oKey].Notes,
                                });
                            }
                            else if (v[oKey] !== null && v[oKey] !== "None" &&
                                oKey.toLowerCase().indexOf("pharmacy") < 0 &&
                                !["bin", "pcn"].includes(oKey.toLowerCase())) {
                                ll.push({
                                    name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                    value: v[oKey],
                                    note: null,
                                });
                            }
                        }
                    }
                    if (ll && ll.length > 0) {
                        return ll.map(l => {
                            return noteText += "\n" + l.name + ": " + l.value;
                        });
                    }
                    return '';
                });
            }

            if (p.PCPAuthInfoSummary) {
                [p.PCPAuthInfoSummary].map(d => {
                    var res = [];
                    for (var pkey in d) {
                        if (d[pkey] !== null && d[pkey] !== "None") {
                            res.push({
                                name: pkey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: d[pkey],
                            });
                        }
                    }
                    if (res && res.length > 0) {
                        return res.map(l => {
                            return noteText += "\n" + l.name + ": " + l.value;
                        });
                    }
                    return null;
                });
            }

            if (p.MiscellaneousInfoSummary) {
                [p.MiscellaneousInfoSummary].map(d => {
                    var res = [];
                    for (var pkey in d) {
                        if (d[pkey] !== null) {
                            res.push({
                                name: pkey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: d[pkey],
                            });
                        }
                    }
                    if (res && res.length > 0) {
                        return res.map(r => {
                            return noteText += "\n" + r.name + ": " + r.value;
                        });
                    }
                    return '';
                });
            }

            if (p.HBPC_Deductible_OOP_Summary) {
                [p.HBPC_Deductible_OOP_Summary].map(v => {
                    var ll = [];
                    for (var oKey in v) {
                        if (v[oKey] && typeof v[oKey] === "object" && v[oKey].hasOwnProperty("Value")) {
                            ll.push({
                                name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: v[oKey].Value,
                            });
                        }
                        else if (v[oKey] !== null) {
                            ll.push({
                                name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: v[oKey],
                            });
                        }
                    }
                    if (ll && ll.length > 0) {
                        return ll.map(l => {
                            return noteText += "\n" + l.name + ": " + l.value;
                        });
                    }
                    return '';
                });
            }

            if (p.DMESummary) {
                [p.DMESummary].map(v => {
                    var ll = [];
                    for (var oKey in v) {
                        if (v[oKey] && typeof v[oKey] === "object" && v[oKey].hasOwnProperty("Value")) {
                            ll.push({
                                name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: v[oKey].Value,
                                note: v[oKey].Notes,
                            });
                        }
                        else if (v[oKey] !== null && v[oKey] !== "None") {
                            ll.push({
                                name: oKey.replace(/[_]/g, ' ').replace(/([a-zA-Z])([A-Z])([a-z])/g, '$1 $2$3'),
                                value: v[oKey],
                                note: null,
                            });
                        }
                    }
                    if (ll && ll.length > 0) {
                        return ll.map(l => {
                            return noteText += "\n" + l.name + ": " + l.value;
                        });
                    }
                    return '';
                });
            }

            noteText += "\n";
        }

        this.setState({
            noteText: noteText,
        }, () => {
            this.handleSaveActivityNote(order);
        });
    }

    renderInternal() {
        const handleIntakeQuestionnaireSave = (intakeQuestionnaire, updatedOrder) => {
            if (intakeQuestionnaire.lastPhysicianVisitDate) {
                intakeQuestionnaire.lastPhysicianVisitDate = moment(intakeQuestionnaire.lastPhysicianVisitDate).format("YYYY-M-D");
                intakeQuestionnaire.currentMobilityAssistiveEquipmentDate = moment(intakeQuestionnaire.currentMobilityAssistiveEquipmentDate).format("YYYY-M-DD");
            }

            const validationIssues = !this.state.readOnly ? Validator.validate(
                intakeQuestionnaire,
                {
                    homeHealth: {
                        'required': true
                    },
                    planToChangeInsurance: {
                        'required': true,
                    },
                    lastPhysicianVisitDate: {
                        'required': true
                    },
                    patientCommitToProcess: {
                        'required': true
                    },
                    reasonForEquipment: {
                        'required': true
                    },
                    currentMobilityAssistiveEquipment: {
                        'required': true
                    },
                    ...(intakeQuestionnaire.currentMobilityAssistiveEquipment === true && {
                        currentMobilityAssistiveEquipmentDate: {
                            'required': true
                        }
                    }),
                }
            ) : {};

            this.setState({
                validationIssues: validationIssues,
            });

            if (Object.keys(validationIssues).length === 0) {
                this.handleUpdateOrderActivity(
                    this.state.row,
                    {
                        "intakeQuestionnaire": {
                            orderActivityId: this.state.row['order_activity.id'],
                            jsonResponse: JSON.stringify(intakeQuestionnaire),
                        },
                    },
                    updatedOrder,
                );
                if (updatedOrder.shouldCreateVerificationNote) {
                    this.handleVerificationNoteCreation(updatedOrder);
                }
            } else {
                NotistackEnqueueSnackbar("Please correct the displayed issues.", { variant: "error" });
            }
        };

        const handleCheckInsuranceEligibility = (order, fieldPrefix) => {
            this.setState({
                [`${fieldPrefix}VerificationLoading`]: true,
            });

            const saveIntakeInsuranceStepToChart = async () => {
                const insuranceUpdate = {
                    id: order.id,
                    insuranceTypeId: order?.insuranceTypeId ?? null,
                    insuranceSubTypeId: order?.insuranceSubTypeId ?? null,
                    insurancePolicy: order?.insurancePolicy ?? null,
                    insuranceGroup: order?.insuranceGroup ?? null,
                    secondaryInsuranceTypeId: order?.hasSecondaryInsurance === false ? null : (order?.secondaryInsuranceTypeId ?? null),
                    secondaryInsuranceSubTypeId: order?.hasSecondaryInsurance === false ? null : (order?.secondaryInsuranceSubTypeId ?? null),
                    secondaryInsurancePolicy: order?.hasSecondaryInsurance === false ? null : (order?.secondaryInsurancePolicy ?? null),
                    secondaryInsuranceGroup: order?.hasSecondaryInsurance === false ? null : (order?.secondaryInsuranceGroup ?? null),
                    lastUpdatedOn: order.lastUpdatedOn,
                };

                await api.send("POST", "/chart/update", {
                    patientId: this.state.row["order_activity.patient_id"],
                    updateData: {
                        orderActivity: {
                            [order.id]: insuranceUpdate,
                        }
                    },
                    returnChart: false,
                }, 0);
            };

            const eligibilityValidationIssues = Validator.validate(
                order,
                {
                    [`${fieldPrefix}Policy`]: {
                        'required': true,
                        ...(order[`${fieldPrefix}TypeId`] && order[`${fieldPrefix}TypeId`] === 1 && { "type": "mbi" })
                    },
                    [`${fieldPrefix}SubTypeId`]: {
                        'required': true,
                    },
                    [`${fieldPrefix}TypeId`]: {
                        'required': true,
                    },
                }
            );

            this.setState({
                eligibilityValidationIssues: eligibilityValidationIssues,
            });

            if (Object.keys(eligibilityValidationIssues).length === 0) {
                saveIntakeInsuranceStepToChart()
                    .then(() => {
                        return api.send("GET", `/order?id=${order.id}`);
                    })
                    .then((refreshedOrder) => {
                        this.setState({
                            order: refreshedOrder,
                        });
                        return refreshedOrder;
                    })
                    .then(() => {
                        return insuranceService.refreshPverifyCoverage(order.id, fieldPrefix === "insurance" ? "primary" : "secondary")
                    })
                    .then(response => {
                        this.setState({
                            [`${fieldPrefix}Verification`]: response,
                            [`${fieldPrefix}VerificationLoading`]: false,
                        })
                    })
                    .catch(err => {
                        if (err?.message?.includes("ORDER_CONFLICT:")) {
                            NotistackEnqueueSnackbar(
                                "This order was recently updated. Please refresh to see the latest changes.",
                                { variant: "warning" }
                            );
                        } else {
                            console.error(err);
                        }
                        this.setState({
                            [`${fieldPrefix}VerificationLoading`]: false,
                        })
                    });
            } else {
                NotistackEnqueueSnackbar("Please correct the displayed issues.", { variant: "error" });
                this.setState({
                    [`${fieldPrefix}VerificationLoading`]: false,
                })
            }
        };

        return (
            <>
                <DialogIntakeQuestionnaire
                    open={this.state.intakeQuestionnaireDialogOpen}
                    onClose={() => {
                        this.setState({
                            insuranceVerification: null,
                            secondaryInsuranceVerification: null,
                            sameSimResults: null,
                            eligibilityValidationIssues: {},
                        });
                        this.handleOnCloseIntakeQuestionnaireDialog();
                    }}
                    row={this.state.row}
                    order={this.state.order}
                    loading={this.state.loading}
                    validationIssues={this.state.validationIssues}
                    onConfirm={(intakeQuestionnaire, updatedOrder) => handleIntakeQuestionnaireSave(intakeQuestionnaire, updatedOrder)}
                    onCheckEligibility={(order, fieldPrefix) => handleCheckInsuranceEligibility(order, fieldPrefix)}
                    onCheckSameSim={(order) => this.handleCheckSameSim(order)}
                    sameSimResults={this.state.sameSimResults}
                    intakeQuestionnaire={this.state.intakeQuestionnaire}
                    readOnly={this.state.readOnly}
                    isLoading={this.state.rowDataLoading}
                    insuranceLoading={this.state.insuranceVerificationLoading}
                    secondaryInsuranceLoading={this.state.secondaryInsuranceVerificationLoading}
                    sameSimVerificationLoading={this.state.sameSimVerificationLoading}
                    insuranceVerification={this.state.insuranceVerification}
                    secondaryInsuranceVerification={this.state.secondaryInsuranceVerification}
                    eligibilityValidationIssues={this.state.eligibilityValidationIssues}
                />
                {this.state.datePickerOpen ? (
                    <DialogDatePicker
                        open={this.state.datePickerOpen}
                        onClose={() => {
                            this.setState({ datePickerOpen: false })
                        }}
                        onConfirm={(value) => {
                            this.handleUpdateOrderActivity(
                                this.state.row,
                                {
                                    [this.state.columnName]: value ? value.format("YYYY-MM-DD") : null,
                                    lastUpdatedById: this.context.currentUser.id,
                                }
                            );
                        }}
                        loading={this.state.loading}
                        defaultValue={this.state.defaultValue}
                    />
                ) : null}
                {this.state.evaluationTypeAndDatePickerOpen ? (
                    <DialogEvaluationTypeAndDatePicker
                        open={this.state.evaluationTypeAndDatePickerOpen}
                        onClose={() => {
                            this.setState({ evaluationTypeAndDatePickerOpen: false })
                        }}
                        row={this.state.row}
                        onConfirm={(value) => {
                            this.handleUpdateOrderActivity(
                                this.state.row,
                                {
                                    evaluationDate: value ? value.format("YYYY-MM-DD") : null,
                                    lastUpdatedById: this.context.currentUser.id,
                                }
                            );
                        }}
                        loading={this.state.loading}
                        defaultValue={this.state.defaultValue}
                    />
                ) : null}
                {this.state.atpDateTimePickerOpen ? (
                    <DialogAtpDateTimePicker
                        open={this.state.atpDateTimePickerOpen}
                        onClose={() => {
                            this.setState({ atpDateTimePickerOpen: false })
                        }}
                        row={this.state.unformattedRow}
                        onConfirm={(data) => {
                            if (!data) {
                                data = { timestamp: null, atpId: null };
                            }
                            this.handleUpdateOrderActivity(
                                this.state.row,
                                {
                                    atpEvaluationDate: data.timestamp ? data.timestamp : null,
                                    atpId: data.atpId ? data.atpId : null,
                                    lastUpdatedById: this.context.currentUser.id,
                                },
                                null,
                                this.state.unformattedRow
                            );
                        }}
                        loading={this.state.loading}
                        defaultValue={this.state.defaultValue}
                        maxDate={moment().add(1, 'year')
                            .format("YYYY-MM-DD")
                        }
                    />
                ) : null}
            </>
        );
    }

    /**
    * @return {array} Order activity actions.
    */
    getActionsInternal() {
        let actions = [];

        actions.push(
            <MUIMenuItem value={"Create Patient"} onClick={() => { this.props.history.push({ pathname: "/patient/create" }); }} >
                <MUIListItemIcon>
                    <MUIPersonAddIcon fontSize="small" />
                </MUIListItemIcon>
                Create Patient
            </MUIMenuItem>
        );

        return actions;
    }

    /**
     * Get any applicable group summary items for the data grid. This is used to
     * add custom values to the grouping rows.
     */
    getGroupSummaryItems() {
        return [{
            columnName: 'order_activity.order_activity_products._sum_points',
            type: 'sumPoints',
            showInGroupFooter: false
        }];
    }
}
